import React, { useState, useContext } from "react";
import {
  Modal,
  Form,
  Input,
  Upload,
  Button,
  Row,
  Col,
  Divider,
  Typography,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { CgProfile } from "react-icons/cg";
import axios from "axios";
import { UserDataContext } from "../../context/User";
import { useNavigate } from "react-router-dom";

const { Text, Link } = Typography;

const AuthModal = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isRegister, setIsRegister] = useState(true);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const { login, logOut, userData } = useContext(UserDataContext);

  // Switch between Register / Sign In
  const switchMode = () => {
    form.resetFields();
    setIsRegister(!isRegister);
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        if (isRegister) handleRegister(values);
        else handleLogin(values);
      })
      .catch(() => {});
  };

  // --- Register Handler ---
  const handleRegister = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries({
        firstName: values.firstName,
        lastName: values.lastName,
        username: values.username,
        email: values.email,
        password: values.password,
        role: "user",
      }).forEach(([k, v]) => formData.append(k, v));

      if (values.pfp && values.pfp[0]?.originFileObj) {
        formData.append("pfp", values.pfp[0].originFileObj);
      }

      const { data } = await axios.post("/api/auth/register", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (data.success) {
        messageApi.success(data.message || "Registration successful!");
        form.resetFields();
        setIsRegister(false);
      } else messageApi.error(data.message || "Registration failed");
    } catch (err) {
      console.error("[Register Error]:", err);

      // Extract backend message if available
      const backendMessage =
        err.response?.data?.message ||
        err.message ||
        "Registration failed. Try again.";

      messageApi.error(backendMessage);
    } finally {
      setLoading(false);
    }
  };

  // --- Login Handler ---
  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const response = await login({
        emailOrUsername: values.emailOrUsername,
        password: values.password,
        from: "web",
      });

      if (response?.success) {
        messageApi.success("Login successful!");
        setOpen(false);
        form.resetFields();
        navigate("/designer");
      } else {
        messageApi.error(response?.message || "Login failed. Try again.");
      }
    } catch (err) {
      console.error("[Login Error]:", err);

      const backendMessage =
        err.response?.data?.message ||
        err.message ||
        "Login failed. Try again.";

      messageApi.error(backendMessage);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <>
      {contextHolder}

      {/* Profile / Logout Button */}
      <li className="nav-link">
        {userData ? (
          <button
            onClick={logOut}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-[#1ab7ea] rounded-full shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
          >
            Logout
          </button>
        ) : (
          <button
            onClick={() => setOpen(true)}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-[#1ab7ea]/10 to-[#1877F2]/10 hover:from-[#1ab7ea]/30 hover:to-[#1877F2]/30 transition-all duration-300"
          >
            <CgProfile className="text-[#1877F2] text-2xl transition-transform duration-300 hover:scale-110" />
          </button>
        )}
      </li>

      {/* Auth Modal */}
      <Modal
        title={
          <span className="text-xl font-semibold text-[#1ab7ea]">
            {isRegister ? "Create Your Account" : "Sign In to Your Account"}
          </span>
        }
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleOk}
        okText={isRegister ? "Register" : "Sign In"}
        confirmLoading={loading}
        cancelText="Cancel"
        centered
        className="custom-auth-modal"
      >
        <Divider className="mb-4 border-[#1ab7ea]/40" />

        <Form
          form={form}
          layout="vertical"
          name="authForm"
          className="space-y-2"
          initialValues={{ role: "user" }}
        >
          {isRegister ? (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="First Name"
                    name="firstName"
                    rules={[
                      { required: true, message: "Enter your first name" },
                    ]}
                  >
                    <Input
                      placeholder="John"
                      className="rounded-lg focus:border-[#1ab7ea] focus:shadow-md transition-all"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Last Name"
                    name="lastName"
                    rules={[
                      { required: true, message: "Enter your last name" },
                    ]}
                  >
                    <Input
                      placeholder="Doe"
                      className="rounded-lg focus:border-[#1ab7ea] focus:shadow-md transition-all"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Username"
                name="username"
                rules={[{ required: true, message: "Choose a username" }]}
              >
                <Input
                  placeholder="johndoe123"
                  className="rounded-lg focus:border-[#1ab7ea] focus:shadow-md transition-all"
                />
              </Form.Item>

              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Enter your email" },
                  { type: "email", message: "Enter a valid email" },
                ]}
              >
                <Input
                  placeholder="example@email.com"
                  className="rounded-lg focus:border-[#1ab7ea] focus:shadow-md transition-all"
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[{ required: true, message: "Enter your password" }]}
              >
                <Input.Password
                  placeholder="••••••••"
                  className="rounded-lg focus:border-[#1ab7ea] focus:shadow-md transition-all"
                />
              </Form.Item>

              <Form.Item
                label="Profile Picture"
                name="pfp"
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList}
              >
                <Upload
                  name="pfp"
                  beforeUpload={() => false}
                  listType="picture-card"
                  className="rounded-lg"
                >
                  <Button
                    icon={<UploadOutlined />}
                    className="bg-[#1ab7ea] text-white font-medium border-none shadow-md hover:shadow-lg hover:bg-[#1877F2] transition-all duration-300"
                  >
                    Upload
                  </Button>
                </Upload>
              </Form.Item>

              <div className="text-center mt-4">
                <Text className="text-gray-600">
                  Already have an account?{" "}
                  <Link
                    onClick={switchMode}
                    className="text-[#1ab7ea] hover:text-[#1877F2]"
                  >
                    Sign in
                  </Link>
                </Text>
              </div>
            </>
          ) : (
            <>
              <Form.Item
                label="Email or Username"
                name="emailOrUsername"
                rules={[
                  { required: true, message: "Enter your email or username" },
                ]}
              >
                <Input
                  placeholder="Enter your email or username"
                  className="rounded-lg focus:border-[#1ab7ea] focus:shadow-md transition-all"
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[{ required: true, message: "Enter your password" }]}
              >
                <Input.Password
                  placeholder="••••••••"
                  className="rounded-lg focus:border-[#1ab7ea] focus:shadow-md transition-all"
                />
              </Form.Item>

              <div className="flex justify-between items-center mt-2">
                <Text className="text-gray-600">
                  Don’t have an account?{" "}
                  <Link
                    onClick={switchMode}
                    className="text-[#1ab7ea] hover:text-[#1877F2]"
                  >
                    Register
                  </Link>
                </Text>
                <Link className="text-[#1ab7ea] hover:text-[#1877F2] font-medium">
                  Forgot Password?
                </Link>
              </div>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default AuthModal;
